import axios from 'axios'
import moment from 'moment-timezone'
import { getVersion } from '@/libs/version'
import { executionsPath, executionPath } from '@/libs/buttonExecution'

export const INVALID_CUSTOMER = 'ERROR.PROVISION.INVALID_CUSTOMER'

export default {
  async setConfiguration (customer, authorization) {
    const vm = this
    vm.customer = customer
    vm.authorization = authorization

    vm.api = axios.create({
      baseURL: `https://${customer}.api.vonixcc.com.br`,
      timeout: 30000
    })

    vm.api.interceptors.request.use((config) => {
      if (vm.authorization) config.headers.Authorization = `${vm.authorization.type} ${vm.authorization.token}`
      return config
    })

    vm.api.interceptors.response.use((res) => {
      if (!res?.data) return res
      return res.data?.data ?? res.data
    })

    return vm
  },

  getAttachmentUrl (attachmentId, queryParams) {
    const { customer, authorization: { token } } = this
    if (queryParams) return `https://${customer}.api.vonixcc.com.br/vonix-phone/attachments?key=${queryParams}&token=${token}`
    return `https://${customer}.api.vonixcc.com.br/cc/bots/messages/${attachmentId}/attachment?token=${token}`
  },

  getUrlPDF (queryParams) {
    const { customer, authorization: { token } } = this
    return `https://${customer}.api.vonixcc.com.br/vonix-phone/attachments?key=${queryParams}&requestType=gets3Path&token=${token}`
  },

  async resolveS3Url (url) {
    if (!url || !url.includes('requestType=gets3Path')) return url
    try {
      const response = await fetch(url)
      const { data } = await response.json()
      return data || url
    } catch (error) {
      console.error('Erro ao resolver URL S3:', error)
      return url
    }
  },

  getSignedURL (params) {
    const queryString = new URLSearchParams()

    Object.keys(params).forEach((key) => {
      queryString.append(key, params[key])
    })

    return `https://${this.customer}.api.vonixcc.com.br/vonix-phone/attachments/signed-url?${queryString.toString()}`
  },

  async gets3Path (url) {
    const location = await this.api.get(url, { params: { requestType: 'gets3Path' } })
    return location
  },

  getAuthorization () {
    return this.authorization ?? {}
  },

  setAuthorization (authorization) {
    this.authorization = authorization
  },

  async getEntryNumbers (getExtensionStatus = true) {
    return this.api.get(`/vonix-phone/entrypointers?getExtensionStatus=${getExtensionStatus}`).then((entryNumbers) => {
      const data = entryNumbers.data ?? entryNumbers
      return data
    })
  },

  loadCall (callId, params) {
    return this.api.get(`/vonix-phone/calls/${callId}`, { params })
  },

  getSession (agentId, isLogin = false) {
    const version = getVersion()
    const midnight = moment().startOf('day').unix()
    return this.api.get(`/vonix-phone/session/${agentId}`, { params: { midnight, version, isLogin, skipChatMessages: true } })
  },

  getProfiler (payload) {
    return this.api.post('/profilers/attendance/load', payload)
  },

  execHooks (profilerId, trigger, payload) {
    return this.api.post(`/profilers/${profilerId}/hooks/exec/${trigger}`, payload)
  },

  startButtonExecution (profilerId, hookId, payload) {
    return this.api.post(executionsPath(profilerId, hookId), payload)
  },

  getButtonExecution (profilerId, hookId, executionId) {
    return this.api.get(executionPath(profilerId, hookId, executionId))
  },

  finishChat (chatId, payload) {
    return this.api.put(`/bots/chatmessages/${chatId}/finish`, payload)
  },

  postAnswerProfiler (profiler) {
    return this.api.post(`/profilers/${profiler.id}/answers`, profiler)
  },

  initiateChat (payload) {
    return this.api.post('/bots/chats', payload)
  },

  s3SignedPost (params) {
    return this.api.post('/vonix-phone/attachments/signed-url', null, { params })
  },

  loadEmailMessages (emailAttendanceId) {
    return this.api.get(`/vonix-phone/attendances/${emailAttendanceId}/emails`)
  },

  loadBodyMessage (emailMessageId) {
    return this.api.get(`/vonix-phone/email/${emailMessageId}/body`)
  },

  loadChatMessages (chatId, params) {
    return this.api.get(`/vonix-phone/attendances/${chatId}/chats`, { params })
  },

  loadAttendanceType (contact) {
    return this.api.get(`/vonix-phone/contact/${contact}/locality`)
  },

  loadAttendanceFrequency (contact, params) {
    return this.api.get(`/vonix-phone/contact/${contact}/frequency`, { params })
  },

  loadAttendanceHistory (page = 1, contact, queues) {
    return this.api.get(`/vonix-phone/history?page=${page}&contactNumber=${contact}&queues=${queues}`)
  },

  putChatsInfo (payload) {
    return this.api.put('/cc/chats/info', payload)
  },

  getChatInfo (chatId) {
    return this.api.get(`/vonix-phone/chats/${chatId}/info`)
  },

  loadAgentQueues (agentId) {
    return this.api.get(`/vonix-phone/agents/${agentId}/queues`)
  },

  getAgentQueueConfig (agentId) {
    return this.api.get(`/vonix-phone/agents/${agentId}/queue-config`)
  },

  getQueues () {
    return this.api.get('/queues', { params: { limit: 9999, ura: false, active: true } })
  },

  getAppointments (params) {
    return this.api.get('/vonix-phone/schedule', { params })
  },

  saveAppointment (payload) {
    return this.api.post('/vonix-phone/schedule', payload)
  },

  patchAppointments (payload) {
    return this.api.patch('/vonix-phone/schedule', payload)
  },

  patchAgentQueues (agentId, queues) {
    return this.api.patch(`/agents/${agentId}`, { queues })
  },

  getQueuesProperties (queueList) {
    return this.api.get(`/vonix-phone/queues-properties?queues=${queueList}`)
  },

  postCallbackCalls (params) {
    return this.api.post('/vonix-phone/callback-calls', params)
  },

  async getRecordingS3Url (callId, fileName) {
    const { token } = this.authorization || {}
    if (!token) return null

    const queryParams = new URLSearchParams({
      filename: fileName,
      requestType: 'gets3Path',
      token
    }).toString()

    const response = await axios.get(`https://${this.customer}.api.vonixcc.com.br/calls/${callId}/recording?${queryParams}`)
    return response.data?.data
  },

  async getNextPreviewContact (queueId, agentId) {
    return this.api.get(`preview/next-contact?queueId=${queueId}&agentId=${agentId}`).catch(err => {
      console.log(err)
      return {}
    })
  },

  async postPreviewStatus (contactId, status, extraInfo = null) {
    const payload = {}
    payload.status = status
    if (extraInfo) payload.extraInfo = extraInfo
    return this.api.patch(`/preview/contact/${contactId}/status`, payload)
  },

  async postPreviewAttendances (payload) {
    return this.api.post('/preview/attendance', payload)
  },

  async getDownloadUrl (url) {
    const urlString = String(url)
    const isS3Url = urlString.includes('.s3.') && urlString.includes('amazonaws.com') && urlString.includes('X-Amz-Algorithm')
    const isApiAttachmentUrl = urlString.includes('api.vonixcc.com.br/vonix-phone/attachments')

    if (isS3Url) {
      const s3Url = new URL(urlString)
      return `${s3Url.origin}${s3Url.pathname}`
    }

    if (isApiAttachmentUrl) {
      return this.getPresignedUrl(urlString)
    }

    return url
  },

  async getPresignedUrl (apiUrl) {
    try {
      const url = new URL(apiUrl)
      if (!url.searchParams.has('requestType')) {
        url.searchParams.set('requestType', 'gets3Path')
      }
      const presignedUrl = await this.api.get(url.href)
      const s3Url = new URL(presignedUrl)
      return `${s3Url.origin}${s3Url.pathname}`
    } catch (error) {
      console.error('Erro ao obter a URL S3:', error)
      return null
    }
  },

  async postEmailReview (payload) {
    return this.api.post('/vonix-phone/email-review', payload)
  }
}
